/**
 * Widget configuration payload builders, ported from redux/apps/sagas.js.
 *
 * Kept out of sagas.ts so the saga reads as control flow: these are pure functions
 * over an app + a form's values, and are unit-tested directly.
 */
import { checkIfFeatureRestricted } from 'helpers'
import {
  MAIN_SETTING_KEYS,
  WidgetConfiguration,
  WidgetMainConfig,
  WidgetSidebarConfig,
  WidgetStyleConfig,
} from 'services/chatWidgets'
import constants from '../../constants'

type App = Record<string, unknown>
type FormValues = Record<string, unknown>

const allowed = (app: App, ...features: string[]): boolean =>
  !features.every(feature => checkIfFeatureRestricted(app, feature))

/**
 * Sidebar keys whose form field is prefixed — the form namespaces these to avoid
 * colliding with the same-named `main` fields on the sibling tab.
 */
const SIDEBAR_PREFIXED_KEYS = ['chats', 'users', 'groups', 'calls', 'user_settings'] as const

const SIDEBAR_PLAIN_KEYS = [
  'recent_chat_listing',
  'user_listing',
  'group_listing',
  'sidebar_navigation_sequence',
  'start_a_new_conversation',
] as const

/** Tab order a widget starts with, and the fallback when a stored record omits it. */
export const DEFAULT_NAV_SEQUENCE = ['chats', 'users', 'groups', 'calls', 'settings'] as const

/** Fixed palette applied on every Customize save — the colour pickers are not wired up. */
const DEFAULT_STYLE_COLORS = {
  primary_color: '#03A9F4',
  foreground_color: '#000000',
  background_color: '#FFFFFF',
  override_system_background_colors: true,
} as const

/**
 * Builds the `style` sub-tree from the Customize form (selectedComponent GLOBAL).
 * The colour pickers for primary/foreground/background are not wired up, so those
 * keys fall back to `existing` (preserving any value set outside this form) and
 * only default to `DEFAULT_STYLE_COLORS` for a widget that has none yet.
 */
export function buildStyleSettings(
  values: FormValues,
  existing?: WidgetStyleConfig,
): WidgetStyleConfig {
  return {
    primary_color: existing?.primary_color ?? DEFAULT_STYLE_COLORS.primary_color,
    foreground_color: existing?.foreground_color ?? DEFAULT_STYLE_COLORS.foreground_color,
    background_color: existing?.background_color ?? DEFAULT_STYLE_COLORS.background_color,
    override_system_background_colors:
      existing?.override_system_background_colors ??
      DEFAULT_STYLE_COLORS.override_system_background_colors,
    custom_css: values.custom_css as string,
    custom_js: values.custom_js as string,
    docked_layout_icon_open: values.docked_layout_icon_open as string,
    docked_layout_icon_close: values.docked_layout_icon_close as string,
    docked_layout_icon_background: values.docked_layout_icon_background as string,
  }
}

/** Builds the `sidebar` sub-tree; `existing` preserves keys with no form field. */
export function buildSidebarSettings(
  values: FormValues,
  existing?: WidgetSidebarConfig,
): WidgetSidebarConfig {
  const settings: WidgetSidebarConfig = {}
  SIDEBAR_PLAIN_KEYS.forEach(key => {
    const value = values[key]
    settings[key] = (value !== undefined ? value : existing?.[key]) as never
  })
  SIDEBAR_PREFIXED_KEYS.forEach(key => {
    const value = values[`sidebar_${key}`]
    settings[key] = (value !== undefined ? value : existing?.[key]) as never
  })
  return settings
}

/**
 * Builds the `main` sub-tree from the Settings > Chat Component form. A few keys have
 * no form field (`existing` supplies their prior value instead, so they aren't dropped).
 */
export function buildMainSettings(values: FormValues, existing?: WidgetMainConfig): WidgetMainConfig {
  const settings: WidgetMainConfig = {}
  MAIN_SETTING_KEYS.forEach(key => {
    const value = values[key]
    settings[key] = (value !== undefined ? value : existing?.[key]) as boolean
  })
  return settings
}

/**
 * The configuration a brand-new widget starts with. Call-related and extension-backed
 * defaults are switched off when the app's plan does not include the feature, so a
 * new widget never advertises something the app cannot do.
 */
export function buildDefaultWidgetConfiguration(app: App, name: string): WidgetConfiguration {
  const voice = allowed(app, 'one_on_one_voice_call', 'group_voice_call')
  const video = allowed(app, 'one_on_one_video_call', 'group_video_call')
  const anyCalling = allowed(
    app,
    'one_on_one_voice_call',
    'group_voice_call',
    'one_on_one_video_call',
    'group_video_call',
  )

  return {
    version: 'v2',
    name,
    style: {
      ...DEFAULT_STYLE_COLORS,
      custom_js: '',
      custom_css: '',
      docked_layout_icon_open: constants.chatWidgetDefaults.docked_layout_icon_open,
      docked_layout_icon_close: constants.chatWidgetDefaults.docked_layout_icon_close,
      docked_layout_icon_background: constants.chatWidgetDefaults.docked_layout_icon_background,
    },
    sidebar: {
      recent_chat_listing: 'all_chats',
      user_listing: 'all_users',
      group_listing: 'public_and_password_protected_groups',
      chats: true,
      users: true,
      groups: true,
      calls: false,
      user_settings: false,
      sidebar_navigation_sequence: [...DEFAULT_NAV_SEQUENCE],
      start_a_new_conversation: 'all_chats',
    },
    main: {
      enable_sending_messages: true,
      allow_mention_members: false,
      enable_editing_messages: true,
      enable_deleting_messages: true,
      hide_deleted_messages: false,
      enable_replying_to_messages: false,
      enable_threaded_replies: !checkIfFeatureRestricted(
        app,
        'threaded_conversations_and_replies',
      ),
      enable_share_copy_forward_messages: false,
      highlight_messages_from_moderators: false,
      enable_voice_calling: voice,
      enable_video_calling: video,
      show_call_recording_option: false,
      send_photos_videos: !checkIfFeatureRestricted(app, 'share_files_and_multimedia'),
      send_files: true,
      send_voice_notes: !checkIfFeatureRestricted(app, 'record_send_voice_messages'),
      send_emojis: !checkIfFeatureRestricted(app, 'extension_emojis'),
      show_emojis_in_larger_size: true,
      send_gifs: false,
      share_live_reactions: !checkIfFeatureRestricted(app, 'live_reactions'),
      share_location: false,
      set_groups_in_qna_mode_by_moderators: false,
      create_groups: true,
      allow_add_members: true,
      join_or_leave_groups: true,
      hide_join_leave_notifications: false,
      allow_delete_groups: true,
      view_group_members: true,
      view_shared_media: true,
      send_message_in_private_to_group_member: false,
      show_typing_indicators: !checkIfFeatureRestricted(app, 'typing_indicators'),
      show_delivery_read_indicators: !checkIfFeatureRestricted(app, 'read_receipts'),
      show_user_presence: !checkIfFeatureRestricted(app, 'user_presence'),
      show_call_notifications: anyCalling,
      block_user: !checkIfFeatureRestricted(app, 'user_to_user_blocking'),
      allow_moderator_to_delete_member_messages: false,
      allow_kick_ban_members: !(
        checkIfFeatureRestricted(app, 'group_moderators') ||
        checkIfFeatureRestricted(app, 'group_kick_member') ||
        checkIfFeatureRestricted(app, 'group_ban_member')
      ),
      allow_promote_demote_members: !checkIfFeatureRestricted(app, 'group_moderators'),
      allow_creating_polls: false,
      enable_sound_for_calls: anyCalling,
      enable_sound_for_messages: true,
      show_stickers: false,
      allow_message_reactions: false,
      enable_collaborative_document: false,
      enable_collaborative_whiteboard: false,
      enable_message_translation: false,
      send_reply_in_private_to_group_member: false,
    },
  }
}

/**
 * Maps a stored configuration back onto the Settings form's field names, so the
 * form can be re-seeded once the widget arrives. `Form.Item initialValue` is
 * captured at first mount — before the fetch resolves — so without this the
 * Sidebar tab shows defaults and publishing would overwrite the stored values.
 */
export function buildSettingsFormValues(configuration: {
  sidebar?: Record<string, unknown>
  main?: Record<string, unknown>
}): FormValues {
  const values: FormValues = {}
  const { sidebar, main } = configuration || {}
  if (sidebar) {
    SIDEBAR_PLAIN_KEYS.forEach(key => {
      if (sidebar[key] !== undefined) values[key] = sidebar[key] as never
    })
    SIDEBAR_PREFIXED_KEYS.forEach(key => {
      if (sidebar[key] !== undefined) values[`sidebar_${key}`] = sidebar[key] as never
    })
  }
  if (main) {
    MAIN_SETTING_KEYS.forEach(key => {
      if (main[key] !== undefined) values[key] = main[key] as never
    })
  }
  return values
}
